<template>
    <NuxtLink :to="{ name: 'products-slug', params: { slug: product.slug } }"
              :aria-label="`View details for ${product.name}`"
              class="card relative w-full max-w-[300px] text-clip rounded-lg bg-white/50 no-underline shadow
                     [content-visibility:auto] [contain-intrinsic-size:300px_400px]">
        <figure class="group relative w-full">
            <!-- thumbhash placeholder as background, real image fades in over it; stays native lazy -->
            <div v-if="product.images.length"
                 ref="imageWrapper"
                 class="aspect-square w-full relative"
                 :style="placeholder(product.images[0]!.meta.thumbhash)">
                <NuxtImg :src="product.images.at(0)!.source"
                         width="300"
                         :loading="eager ? 'eager' : 'lazy'"
                         :fetchpriority="eager ? 'high' : undefined"
                         class="absolute inset-0 aspect-square origin-center object-cover size-full
                                transition-opacity duration-500"
                         :class="{ 'opacity-0': !imageLoaded }"
                         :alt="product.name"
                         @load="imageLoaded = true" />
            </div>
            <figcaption v-else
                        class="flex w-full flex-col items-center justify-center space-y-2 bg-base-300/70
                               text-base-content/40"
                        style="height: 250px">
                <Logo class="size-10" />
                <p class="text-center">
                    No image yet.
                </p>
            </figcaption>
            <figcaption v-if="product.shortDescription"
                        :title="product.shortDescription"
                        class="absolute inset-0 flex flex-col justify-center
                        bg-base-300/80 p-2 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
                <p class="text-ellipsis text-center leading-relaxed text-base-content/70">
                    {{ shortDescription }}
                </p>
            </figcaption>
        </figure>
        <div class="card-body">
            <h2 class="card-title justify-center text-sm 2xs:text-base sm:justify-start sm:text-lg"
                :style="{ 'view-transition-name': `${product.slug}-title` }">
                <ClientOnly>
                    <span class="inline-flex" v-if="isFavourite">
                        <Icon name="mdi:heart" class="text-red-600 size-6" />
                        <span class="sr-only">This is one of your favourite products</span>
                    </span>
                </ClientOnly>
                <span :title="product.name">{{ product.name }}</span>
            </h2>
            <p v-if="product.variations.length > 1"
               class="text-center text-xs italic text-base-content xs:text-sm sm:text-left">
                <span class="sr-only">This product has</span>
                {{ product.variations.length }} options available
            </p>
        </div>
    </NuxtLink>
</template>

<script setup lang="ts">
import type { ProductAttributes } from '~/types'
import { words } from '~/lib/upfront'

defineOptions({
    name: 'ProductTile'
})

const props = defineProps({
    product: {
        type: Object as PropType<ProductAttributes>,
        required: true
    },
    // above-the-fold tiles: the first one is the page's LCP, lazy loading only delays it
    eager: Boolean
})
const shortDescription = computed(() => words(props.product.shortDescription, 27))
const { isFavourite } = useProductFavourites(props.product.id)

const placeholder = useThumbhash()
const imageLoaded = ref(false)
const imageWrapper = useTemplateRef('imageWrapper')
// an SSR'd image can finish loading before hydration attaches @load
onMounted(() => {
    if (imageWrapper.value?.querySelector('img')?.complete) {
        imageLoaded.value = true
    }
})
</script>
